import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../services/api";
import MobileAppLayout from "../layouts/MobileAppLayout";
import { GameCard, GameHero } from "../components/game";
import Growie from "../components/Growie";
import PasswordMeter from "../components/PasswordMeter";
import { athleteAges } from "../lib/athleteAge";

type JoinInfo = {
  team: { id: string; name: string; age_group: string | null };
  club: { id: string; name: string; logo_url: string | null };
  sport: { id: string; name: string } | null;
};

type Child = { id: string; full_name: string | null; username: string | null; email: string };

/** Same range as the public form: the ages a club actually fields. */
// 2 to 18 — see lib/athleteAge.
const AGES = athleteAges();

const field =
  "w-full px-3 py-2.5 text-sm bg-white border border-slate-300 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500";

/**
 * The signed-in half of joining through a shared link.
 *
 * A parent who already has an account lands here, because the public form
 * refuses to check anyone's password. From here they either point at a child
 * they already have — a family training at two clubs, or moving between them —
 * or add a new one.
 */
export default function JoinTeamClaim() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [info, setInfo] = useState<JoinInfo | null>(null);
  const [children, setChildren] = useState<Child[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ club: string; team: string } | null>(null);

  const [mode, setMode] = useState<"existing" | "new">("existing");
  const [childId, setChildId] = useState("");
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [idMode, setIdMode] = useState<"username" | "email">("username");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [i, kids] = await Promise.all([
          api.get<JoinInfo>(`/public/join/${token}`),
          api.get<{ children: Child[] }>("/parent/children-list").catch(() => ({ data: { children: [] } })),
        ]);
        setInfo(i.data);
        const list = kids.data.children || [];
        setChildren(list);
        // With no children on the account there is nothing to pick, so start
        // on the form that actually applies.
        if (list.length === 0) setMode("new");
        else setChildId(list[0].id);
      } catch (e: any) {
        setLoadError(e?.response?.data?.error || "Linkul nu mai este valabil.");
      }
      setLoading(false);
    })();
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload =
        mode === "existing"
          ? { child_id: childId }
          : {
              child: {
                first_name: name.trim(),
                age: Number(age),
                username: idMode === "username" ? username.trim() : undefined,
                email: idMode === "email" ? email.trim() : undefined,
                password,
              },
            };
      const { data } = await api.post(`/join-links/claim/${token}`, payload);
      setDone({ club: data.club, team: data.team });
    } catch (e: any) {
      setError(e?.response?.data?.error || "Nu am putut finaliza înscrierea.");
    }
    setSubmitting(false);
  };

  const body = () => {
    if (loading) return <p className="text-sm text-slate-500">Se încarcă...</p>;

    if (loadError || !info) {
      return (
        <>
          <h2 className="text-lg font-bold text-slate-800">Link indisponibil</h2>
          <p className="mt-2 text-sm text-slate-600">{loadError}</p>
          <button
            type="button"
            onClick={() => navigate("/app/dashboard")}
            className="mt-4 text-sm font-semibold text-indigo-600"
          >
            Înapoi
          </button>
        </>
      );
    }

    if (done) {
      return (
        <div className="text-center">
          <Growie pose="cheer" className="w-24 h-24 mx-auto" title="Growie, mascota iGROWth" />
          <h2 className="mt-2 text-xl font-bold text-slate-800">Gata, cererea a plecat!</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Antrenorul de la <strong>{done.club}</strong> confirmă înscrierea în echipa{" "}
            <strong>{done.team}</strong>. Te anunțăm imediat ce se întâmplă — copilul apare în
            echipă după aprobare.
          </p>
          <Link
            to="/app/dashboard"
            className="flex items-center justify-center w-full py-3 mt-5 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700"
          >
            Înapoi la aplicație
          </Link>
        </div>
      );
    }

    return (
      <>
        <div className="px-4 py-3 text-sm border rounded-xl bg-indigo-50 border-indigo-200 text-indigo-900">
          <strong>{info.club.name}</strong> · echipa <strong>{info.team.name}</strong>
          {info.team.age_group ? ` (${info.team.age_group})` : ""}
          <p className="mt-1 text-xs text-indigo-700">
            Clubul confirmă înscrierea înainte ca el să apară în echipă.
          </p>
        </div>

        {error && (
          <div className="px-4 py-3 mt-4 text-sm border rounded-xl bg-rose-50 border-rose-200 text-rose-700">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="mt-4 space-y-4">
          {children.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  ["existing", "Un copil al meu"],
                  ["new", "Adaug un copil nou"],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setMode(key)}
                  className={`px-3 py-2.5 text-sm font-semibold rounded-xl border ${
                    mode === key
                      ? "bg-indigo-50 border-indigo-400 text-indigo-800"
                      : "bg-white border-slate-300 text-slate-600"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {mode === "existing" ? (
            <div>
              <label className="block mb-1 text-sm text-slate-600" htmlFor="claim-child">
                Copilul
              </label>
              <select id="claim-child" value={childId} onChange={(e) => setChildId(e.target.value)} className={field}>
                {children.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.full_name || c.username || c.email}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-400">
                Copilul poate face parte din mai multe cluburi și echipe în același timp.
              </p>
            </div>
          ) : (
            <>
              <div>
                <label className="block mb-1 text-sm text-slate-600" htmlFor="claim-name">
                  Nume și prenume copil
                </label>
                <input id="claim-name" required value={name} onChange={(e) => setName(e.target.value)} className={field} />
              </div>
              <div>
                <label className="block mb-1 text-sm text-slate-600" htmlFor="claim-age">
                  Vârsta
                </label>
                <select id="claim-age" required value={age} onChange={(e) => setAge(e.target.value)} className={field}>
                  <option value="">Alege vârsta</option>
                  {AGES.map((a) => (
                    <option key={a} value={a}>
                      {a} ani
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <span className="block text-sm text-slate-600">Cum se va autentifica în aplicație?</span>
                <p className="mb-2 text-xs text-slate-400">
                  Dacă nu are email propriu, alege opțiunea prin username.
                </p>
                <div className="flex gap-2 mb-2">
                  {(["username", "email"] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setIdMode(m)}
                      className={`px-3 py-1.5 text-sm font-semibold rounded-lg border ${
                        idMode === m
                          ? "bg-indigo-600 border-indigo-600 text-white"
                          : "bg-white border-slate-300 text-slate-600"
                      }`}
                    >
                      {m === "username" ? "Prin Username" : "Prin Email"}
                    </button>
                  ))}
                </div>
                {idMode === "username" ? (
                  <input aria-label="Username copil" required value={username} onChange={(e) => setUsername(e.target.value)} className={field} />
                ) : (
                  <input aria-label="Email copil" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={field} />
                )}
              </div>
              <div>
                <label className="block mb-1 text-sm text-slate-600" htmlFor="claim-pass">
                  Parola copilului
                </label>
                <input id="claim-pass" type="password" required minLength={6} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className={field} />
                <PasswordMeter value={password} />
              </div>
            </>
          )}

          <button
            type="submit"
            disabled={submitting || (mode === "existing" && !childId)}
            className="w-full py-3 text-sm font-bold text-white bg-indigo-600 rounded-xl disabled:opacity-60"
          >
            {submitting ? "Se trimite..." : "Trimite cererea"}
          </button>
        </form>
      </>
    );
  };

  return (
    <MobileAppLayout>
      <div className="pb-8">
        <GameHero flow className="rounded-br-3xl px-4 pt-3 pb-5">
          <h1 className="text-xl font-extrabold">Înscriere în echipă</h1>
          <p className="text-white/70 text-[13px] mt-1.5">
            Ai deja cont, deci alegi doar copilul.
          </p>
        </GameHero>
        <div className="px-4 pt-5">
          <GameCard className="p-5">{body()}</GameCard>
        </div>
      </div>
    </MobileAppLayout>
  );
}
